/* Labels come from the server (policy labels, translated), never from English
   literals here — a blank label stays blank so an inactive policy promises
   nothing and Hindi/other-locale storefronts never fall back to English. Every
   caller's dataset labels are always populated while a policy is active. */
function _resolveEtaFromSeconds(nowIstSeconds, opts) {
  /* Store does not dispatch today (weekly off / holiday). The server already
     resolved the next working day into ncrResolvedLabel, and tomorrowLabel is
     the server's after-cutoff working-day label — both are safe. todayLabel is
     NOT: falling through to it re-promises delivery on a day the store is shut,
     which is the exact bug this whole working-day path exists to prevent.
     fallbackLabel is last because on some call sites it is a cached ETA from
     localStorage, or the today label itself. */
  if (!opts.dispatchToday) {
    return {
      label: opts.ncrResolvedLabel || opts.tomorrowLabel || opts.fallbackLabel || '',
      secsUntilCutoff: null
    };
  }
  if (!opts.hasCutoff) {
    return {
      label: opts.ncrResolvedLabel || opts.fallbackLabel || opts.todayLabel || '',
      secsUntilCutoff: null
    };
  }
  var cutoffSecs = (opts.cutoffHour || 0) * 3600 + (opts.cutoffMinute || 0) * 60;
  var diff = cutoffSecs - nowIstSeconds;
  if (diff > 0) {
    return { label: opts.todayLabel || '', secsUntilCutoff: diff };
  }
  return { label: opts.tomorrowLabel || '', secsUntilCutoff: null };
}

function _getISTSecondsNow() {
  var now = new Date();
  var utcMs = now.getTime() + now.getTimezoneOffset() * 60000;
  var ist = new Date(utcMs + 330 * 60000); /* UTC+5:30 */
  return ist.getHours() * 3600 + ist.getMinutes() * 60 + ist.getSeconds();
}

(function () {
  'use strict';

  var LS_KEY = 'sdp_location';
  var TTL_MS = 6 * 60 * 60 * 1000; /* 6 hours — prevents stale ETA labels across day boundaries */

  /* Customer-facing error text, translated server-side into one JSON meta tag
     (layout/theme.liquid: <meta name="sdp-msgs">). No English literals here; an
     unknown key returns the key itself so an error is never an empty string
     (callers treat a falsy `err` as success). */
  var _msgs = null;
  function _msg(key) {
    if (_msgs === null) {
      _msgs = {};
      try {
        var meta = document.querySelector('meta[name="sdp-msgs"]');
        if (meta && meta.content) _msgs = JSON.parse(meta.content);
      } catch (e) {}
    }
    return _msgs[key] || key;
  }

  function _fire(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { bubbles: false, detail: detail || {} }));
  }

  function _hydrate(data) {
    if (!data) return;
    var root = document.documentElement;
    root.dataset.sdpZone = data.zone || 'india';
    root.dataset.sdpPin  = data.pin  || '';
    root.dataset.sdpCity = data.city || '';
  }

  function _dehydrate() {
    var root = document.documentElement;
    delete root.dataset.sdpZone;
    delete root.dataset.sdpPin;
    delete root.dataset.sdpCity;
  }

  var SindhiLocation = {

    get: function () {
      try {
        var raw = localStorage.getItem(LS_KEY);
        if (!raw) return null;
        var d = JSON.parse(raw);
        if (!d || !d.ts) return null;
        if (Date.now() - d.ts > TTL_MS) {
          localStorage.removeItem(LS_KEY);
          /* The inline hydrator in layout/theme.liquid sets html[data-sdp-zone]
             before first paint; if it ever hydrated from a record that has since
             expired, clear it here too — otherwise cards show NCR labels while the
             location bar renders the "no PIN" state. */
          _dehydrate();
          return null;
        }
        return d;
      } catch (e) { return null; }
    },

    set: function (data) {
      var record = {
        pin:          String(data.pin          || ''),
        zone:         String(data.zone         || 'india'),
        city:         String(data.city         || ''),
        state:        String(data.state        || ''),
        eta_label:    String(data.eta_label    || ''),
        cod_eligible: data.cod_eligible === true,
        cod_surcharge: Number(data.cod_surcharge) || 0,
        ts:           Date.now()
      };
      try { localStorage.setItem(LS_KEY, JSON.stringify(record)); } catch (e) {}
      _hydrate(record);
      _fire('sdp:location:set', record);
    },

    clear: function () {
      try { localStorage.removeItem(LS_KEY); } catch (e) {}
      _dehydrate();
      _fire('sdp:location:clear', {});
    },

    fetch: function (pin, proxyUrl, callback) {
      if (!pin || !/^\d{6}$/.test(String(pin))) {
        if (callback) callback(null, _msg('invalid_pin'));
        return;
      }
      if (!proxyUrl) {
        if (callback) callback(null, _msg('no_proxy'));
        return;
      }
      var sep = proxyUrl.indexOf('?') > -1 ? '&' : '?';
      fetch(proxyUrl + sep + 'pincode=' + encodeURIComponent(pin), { credentials: 'same-origin' })
        .then(function (r) {
          if (!r.ok) return Promise.reject('HTTP ' + r.status);
          return r.json();
        })
        .then(function (json) {
          if (!json || json.zone === 'unknown') {
            if (callback) callback(null, (json && json.message) || _msg('not_serviceable'));
            return;
          }
          var data = {
            pin:           pin,
            zone:          json.zone      || 'india',
            city:          json.city      || '',
            state:         json.state     || '',
            eta_label:     json.eta_label || '',
            cod_eligible:  json.cod_eligible === true,
            cod_surcharge: Number(json.cod_surcharge) || 0
          };
          SindhiLocation.set(data);
          if (callback) callback(data, null);
        })
        .catch(function () {
          if (callback) callback(null, _msg('network'));
        });
    },

    resolveEta: function (opts) {
      return _resolveEtaFromSeconds(_getISTSecondsNow(), opts);
    },

    updateCountdown: function (section, data) {
      var cdEl  = section.querySelector('[data-slb-countdown]');
      var cdSep = section.querySelector('[data-slb-cd-sep]');
      if (!cdEl) return;
      var showCd = section.dataset.showCountdown === 'true';
      var dispToday = section.dataset.dispatchToday === 'true';
      /* No cutoff on the policy = nothing to count down to; never invent one. */
      var hasCutoff = section.dataset.hasCutoff === 'true';
      var cutoffH = parseInt(section.dataset.cutoffHour, 10);
      var cutoffM = parseInt(section.dataset.cutoffMinute || '0', 10);
      var tpl = section.dataset.countdownTpl;
      if (!showCd || data.zone !== 'ncr' || !dispToday || !hasCutoff || isNaN(cutoffH) || !tpl) {
        cdEl.hidden = true;
        if (cdSep) cdSep.hidden = true;
        return;
      }
      var nowMins = Math.floor(_getISTSecondsNow() / 60);
      var cutoffMins = cutoffH * 60 + cutoffM;
      if (nowMins >= cutoffMins) {
        cdEl.hidden = true;
        if (cdSep) cdSep.hidden = true;
        if (section.__cdTimer) { clearInterval(section.__cdTimer); section.__cdTimer = null; }
        return;
      }
      var remMins = cutoffMins - nowMins;
      var rh = Math.floor(remMins / 60);
      var rm = remMins % 60;
      var hoursPart = rh > 0 ? (rh + 'h ') : '';
      cdEl.textContent = tpl.replace('{hours_part}', hoursPart).replace('{minutes}', rm);
      cdEl.hidden = false;
      if (cdSep) cdSep.hidden = false;
    }
  };

  if (typeof window !== 'undefined') {
    window.SindhiLocation = SindhiLocation;

    // Hydrate data-sdp-* attributes immediately (before DOMContentLoaded)
    var _boot = SindhiLocation.get();
    if (_boot) _hydrate(_boot);

    document.addEventListener('DOMContentLoaded', function () {
      // Auto-populate PIN for logged-in customers when no stored PIN exists
      if (!SindhiLocation.get()) {
        var pinMeta   = document.querySelector('meta[name="sdp-customer-pin"]');
        var proxyMeta = document.querySelector('meta[name="sdp-proxy-url"]');
        if (pinMeta && /^\d{6}$/.test(pinMeta.content) && proxyMeta && proxyMeta.content) {
          SindhiLocation.fetch(pinMeta.content, proxyMeta.content, function (data) {
            _fire('sdp:location:ready', data || {});
          });
          return;
        }
      }
      _fire('sdp:location:ready', SindhiLocation.get() || {});
    });
  }

}());

/* Node test harness only — no-op in the browser (module is undefined there). */
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { resolveEtaFromSeconds: _resolveEtaFromSeconds };
}
